"use client";

import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

// Helper function to convert IPFS URLs
const convertToIPFSUrl = (imageUrl: string) => {
  if (!imageUrl) return '';
  // Check if it's already an IPFS gateway URL
  if (imageUrl.startsWith('https://ipfs.io/ipfs/')) return imageUrl;
  
  // Extract IPFS hash from various formats
  let ipfsHash = '';
  if (imageUrl.startsWith('ipfs://')) {
    ipfsHash = imageUrl.replace('ipfs://', '');
  } else if (imageUrl.includes('/ipfs/')) {
    ipfsHash = imageUrl.split('/ipfs/')[1];
  } else {
    // If no IPFS identifier found, return original URL
    return imageUrl;
  }
  
  // Remove any leading/trailing slashes and query parameters
  ipfsHash = ipfsHash.split('?')[0].replace(/^\/+|\/+$/g, '');
  
  return `https://ipfs.io/ipfs/${ipfsHash}`;
};

// Raw data interfaces from data.json
interface NFTTrait {
  v: string;  // value
  c: number;  // count
  r: number;  // rarity score
  p: number;  // percentage
}

interface NFTData {
  i: number;  // id
  n: string;  // name
  m: string;  // image
  r: number;  // global rank
  s: number;  // rarity score
  t: {        // traits
    [traitType: string]: NFTTrait;
  };
}

interface TraitDistribution {
  v: string;  // value
  c: number;  // count
  p: number;  // percentage
  r: number;  // rarity score
}

interface TraitStats {
  t: number;  // total
  c: number;  // count with trait
  p: number;  // percentage with trait
  d: TraitDistribution[];  // distribution
}

interface ChainData {
  t: number;  // total NFTs
  s: {        // trait stats
    [traitType: string]: TraitStats;
  };
  n: NFTData[];  // NFTs
}

interface GlobalStats {
  t: number;     // total NFTs
  y: string[];   // trait types
  r: NFTData[];  // rarity rankings
}

interface Collection {
  n: string;     // collection name
}

interface JsonData {
  a: {           // analytics
    [chainId: string]: ChainData;
  };
  g: GlobalStats;  // global stats
  l: {             // collections
    [collectionId: string]: Collection;
  };
}

// Transformed data interfaces for the UI
interface ChainStats {
  totalNFTs: number;
  uniqueTraits: number;
  rarestScore: number;
  averageScore: number;
  traitDistribution: Array<{
    name: string;
    count: number;
    percentage: number;
    rarityScore: number;
  }>;
  traitCombinations: Array<{
    traits: number;
    count: number;
  }>;
}

interface TransformedData {
  [key: string]: ChainStats;
}

// Search result interfaces
interface SearchResult {
  id: number;
  name: string;
  image: string;
  rank: number;
  score: number;
  traits: {
    type: string;
    value: string;
    rarity: number;
  }[];
  chain: string;
}

export default function ReportPage() {
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState<TransformedData | null>(null);
  const [rawData, setRawData] = useState<JsonData | null>(null);
  const [searchTokenId, setSearchTokenId] = useState('');
  const [searchTraitValue, setSearchTraitValue] = useState('');
  const [selectedChain, setSelectedChain] = useState('global');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  useEffect(() => {
    // Scroll to top on mount
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const response = await fetch('./data.json');
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const text = await response.text();
        try {
          const jsonData: JsonData = JSON.parse(text);
          setRawData(jsonData);
        
          const transformedData: TransformedData = {};
        
          // Process each chain's data
          Object.entries(jsonData.a).forEach(([chainKey, chainData]) => {
            // Calculate trait combinations
            const traitCounts = new Map<number, number>();
            chainData.n.forEach(nft => {
              const traitCount = Object.keys(nft.t || {}).length;
              traitCounts.set(traitCount, (traitCounts.get(traitCount) || 0) + 1);
            });

            console.log(`Chain ${chainKey} trait counts:`, Object.fromEntries(traitCounts));

            // Transform trait combinations into sorted array
            const traitCombinations = Array.from(traitCounts.entries())
              .map(([traits, count]) => ({
                traits: Number(traits),
                count: Number(count)
              }))
              .sort((a, b) => a.traits - b.traits);

            console.log(`Chain ${chainKey} trait combinations:`, traitCombinations);

            transformedData[chainKey] = {
              totalNFTs: chainData.t,
              uniqueTraits: Object.keys(chainData.s).length,
              rarestScore: Math.max(...chainData.n.map(nft => nft.s)),
              averageScore: chainData.n.reduce((acc, nft) => acc + nft.s, 0) / chainData.t,
              traitDistribution: Object.entries(chainData.s)
                .map(([name, trait]) => ({
                  name,
                  count: trait.c,
                  percentage: trait.p,
                  rarityScore: Math.max(...trait.d.map(d => d.r))
                }))
                .sort((a, b) => b.count - a.count),
              traitCombinations
            };
          });

          // Calculate global trait combinations
          if (jsonData.g) {
            const globalTraitCounts = new Map<number, number>();
            Object.values(jsonData.a).forEach(chain => {
              chain.n.forEach(nft => {
                const traitCount = Object.keys(nft.t || {}).length;
                globalTraitCounts.set(traitCount, (globalTraitCounts.get(traitCount) || 0) + 1);
              });
            });

            console.log('Global trait counts:', Object.fromEntries(globalTraitCounts));

            const globalTraitCombinations = Array.from(globalTraitCounts.entries())
              .map(([traits, count]) => ({
                traits: Number(traits),
                count: Number(count)
              }))
              .sort((a, b) => a.traits - b.traits);

            console.log('Global trait combinations:', globalTraitCombinations);

            transformedData.global = {
              totalNFTs: jsonData.g.t,
              uniqueTraits: jsonData.g.y.length,
              rarestScore: Math.max(...jsonData.g.r.map(nft => nft.s)),
              averageScore: jsonData.g.r.reduce((acc, nft) => acc + nft.s, 0) / jsonData.g.t,
              traitDistribution: jsonData.g.y.map(traitType => {
                const allTraits = Object.values(jsonData.a).flatMap(chain => 
                  chain.s[traitType]?.d || []
                );
                return {
                  name: traitType,
                  count: allTraits.reduce((sum, t) => sum + t.c, 0),
                  percentage: allTraits.reduce((sum, t) => sum + t.p, 0) / Object.keys(jsonData.a).length,
                  rarityScore: Math.max(...allTraits.map(t => t.r))
                };
              }).sort((a, b) => b.count - a.count),
              traitCombinations: globalTraitCombinations
            };
          }

          console.log('Final transformed data:', transformedData);
          setData(transformedData);
        } catch (parseError) {
          console.error('Error parsing JSON:', parseError);
          console.error('Raw text received:', text.substring(0, 200) + '...');
          throw new Error('Invalid JSON format');
        }
      } catch (error) {
        console.error('Error loading report data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleTokenSearch = () => {
    if (!rawData || !searchTokenId.trim()) return;
    
    setIsSearching(true);
    const results: SearchResult[] = [];
    const searchTerm = searchTokenId.trim();
    const tokenPattern = new RegExp(`Cryptfluencers #${searchTerm}$`, 'i');
    
    Object.entries(rawData.a).forEach(([chainId, chainData]) => {
      const nft = chainData.n.find(n => tokenPattern.test(n.n));
      if (nft) {
        results.push({
          id: nft.i,
          name: nft.n,
          image: nft.m,
          rank: nft.r,
          score: nft.s,
          traits: Object.entries(nft.t).map(([type, trait]) => ({
            type,
            value: trait.v,
            rarity: trait.r
          })),
          chain: chainId
        });
      }
    });

    setSearchResults(results);
    setIsSearching(false);
  };

  const handleTraitSearch = () => {
    if (!rawData || !searchTraitValue.trim()) return;
    
    setIsSearching(true);
    const results: SearchResult[] = [];
    const searchTerm = searchTraitValue.toLowerCase().trim();
    
    Object.entries(rawData.a).forEach(([chainId, chainData]) => {
      chainData.n.forEach(nft => {
        const hasMatchingTrait = Object.entries(nft.t).some(
          ([_, trait]) => trait.v.toLowerCase().includes(searchTerm)
        );
        
        if (hasMatchingTrait) {
          results.push({
            id: nft.i,
            name: nft.n,
            image: nft.m,
            rank: nft.r,
            score: nft.s,
            traits: Object.entries(nft.t).map(([type, trait]) => ({
              type,
              value: trait.v,
              rarity: trait.r
            })),
            chain: chainId
          });
        }
      });
    });

    setSearchResults(results.sort((a, b) => b.score - a.score).slice(0, 50));
    setIsSearching(false);
  };

  const clearSearch = () => {
    setSearchTokenId('');
    setSearchTraitValue('');
    setSearchResults([]);
  };

  const menuLinks = [
    { name: "Mint NFT", path: "/" },
    { name: "Documentation", path: "/documentation" },
    { name: "FAQ", path: "/faq" },
    { name: "Support", path: "/support" },
    { name: "License", path: "/license" },
    { name: "Privacy Policy", path: "/privacy" },
    { name: "Cookie Policy", path: "/cookies" }
  ];

  const socialLinks = [
    { name: "Twitter", url: "https://x.com/cryptfluencers", icon: "twitter" },
  ];

  if (isLoading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <LoadingSpinner />
          <p className="text-sm text-[#4AFF93] mt-4 font-pixel">LOADING DATA...</p>
          <p className="text-xs text-zinc-500 mt-2">This may take a few moments...</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="text-[#4AFF93] text-4xl font-pixel mb-4">!</div>
          <h2 className="text-[#4AFF93] font-pixel text-xl mb-4">DATA LOADING ERROR</h2>
          <p className="text-zinc-400 mb-4">Unable to load the rarity report data. Please try refreshing the page or contact support if the issue persists.</p>
          <button 
            onClick={() => window.location.reload()} 
            className="px-4 py-2 bg-[#4AFF93] text-black font-pixel text-sm rounded hover:bg-[#4AFF93]/80 transition-colors"
          >
            REFRESH PAGE
          </button>
        </div>
      </div>
    );
  }

  const chains = ['global', 'chia', 'pulse', 'base'];

  const SearchResults = () => {
    if (!searchResults.length) return null;

    return (
      <div className="bg-black/50 backdrop-blur-sm border border-[#9333EA]/20 rounded-lg p-6 mt-6">
        <h3 className="text-sm font-pixel text-[#9333EA] mb-4">
          SEARCH RESULTS ({searchResults.length})
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {searchResults.map((result) => (
            <div 
              key={`${result.chain}-${result.id}`}
              className="bg-black/30 border border-[#9333EA]/10 rounded-lg p-4 space-y-3"
            >
              {result.image && (
                <img 
                  src={convertToIPFSUrl(result.image)}
                  alt={result.name}
                  className="w-full h-48 object-cover rounded-lg"
                />
              )}
              <div>
                <h4 className="font-pixel text-[#9333EA]">{result.name}</h4>
                <p className="text-xs text-zinc-400">Chain: {result.chain.toUpperCase()}</p>
                <p className="text-xs text-zinc-400">Rank: #{result.rank}</p>
                <p className="text-xs text-zinc-400">Score: {result.score.toFixed(2)}</p>
              </div>
              <div className="space-y-1">
                {result.traits.map((trait, idx) => (
                  <div key={idx} className="text-xs">
                    <span className="text-zinc-500">{trait.type}:</span>{' '}
                    <span className="text-white">{trait.value}</span>{' '}
                    <span className="text-emerald-500">(R: {trait.rarity.toFixed(2)})</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Navigation Bar */}
      <nav className="fixed top-0 w-full z-50 backdrop-blur-xl border-b border-white/10">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <a 
            href="/"
            className="text-lg md:text-2xl font-pixel text-[#9333EA] hover:opacity-90 transition-opacity cursor-pointer"
          >
            Cryptfluencers
          </a>
          <div className="flex items-center gap-2">
            <Button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              variant="ghost"
            >
              <div className="space-y-1.5">
                <div className="w-5 h-0.5 bg-current"></div>
                <div className="w-5 h-0.5 bg-current"></div>
                <div className="w-5 h-0.5 bg-current"></div>
              </div>
            </Button>
          </div>
        </div>

        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="absolute top-full left-0 w-full bg-black/95 backdrop-blur-xl border-b border-white/10"
            >
              <div className="container mx-auto py-4 px-4">
                <div className="grid grid-cols-1 gap-2">
                  {menuLinks.map((link) => (
                    <a
                      key={link.path}
                      href={link.path}
                      className="text-sm text-white/80 hover:text-[#9333EA] hover:bg-white/10 px-4 py-2 rounded-lg transition-colors"
                    >
                      {link.name}
                    </a>
                  ))}
                  <div className="h-px bg-white/10 my-2" />
                  <div className="px-4 py-2">
                    <p className="text-sm text-white/60 mb-2">Follow Us</p>
                    {socialLinks.map((link) => (
                      <a
                        key={link.name}
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 text-sm text-white/80 hover:text-white hover:bg-white/10 px-4 py-2 rounded-lg transition-colors"
                      >
                        <span>{link.name}</span>
                        <span className="text-xs text-white/60">↗</span>
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      <div className="absolute inset-0 bg-[url('/grid.svg')] bg-center [mask-image:linear-gradient(180deg,white,rgba(255,255,255,0))]" />
      
      {/* Adjust top padding to account for fixed nav */}
      <div className="container mx-auto px-4 relative z-10 pt-28 pb-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-7xl mx-auto"
        >
          <div className="space-y-8">
            {/* Header */}
            <div>
              <h1 className="text-4xl font-pixel text-[#9333EA] mb-2">xChain Rarity Report</h1>
              <p className="text-sm text-zinc-500 font-pixel">v1.0a</p>
              <p className="mt-4 text-zinc-400 max-w-2xl">
                Check the rarity of the entire collection across all chains. All rarities are based on complete collection.
              </p>
            </div>

            {/* Dashboard Stats */}
            <div className="space-y-6">
              {/* Global Stats - Large Hero Section */}
              <div className="bg-black/50 backdrop-blur-sm border border-[#9333EA]/20 rounded-lg p-8">
                <div className="flex items-center justify-between mb-8">
                  <h2 className="text-2xl font-pixel text-[#9333EA]">GLOBAL STATISTICS</h2>
                  <div className="px-4 py-2 bg-[#9333EA]/10 rounded-lg">
                    <p className="text-sm text-[#9333EA] font-pixel">LIVE DATA</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
                  <div className="bg-black/30 rounded-lg p-6 border border-[#9333EA]/10">
                    <p className="text-sm text-zinc-500 font-pixel mb-2">TOTAL NFTs</p>
                    <p className="text-4xl font-pixel text-[#4AFF93] mb-1">
                      {data?.global?.totalNFTs.toLocaleString() || 0}
                    </p>
                    <p className="text-xs text-zinc-600">Across All Chains</p>
                  </div>
                  <div className="bg-black/30 rounded-lg p-6 border border-[#9333EA]/10">
                    <p className="text-sm text-zinc-500 font-pixel mb-2">UNIQUE TRAITS</p>
                    <p className="text-4xl font-pixel text-[#4AFF93] mb-1">
                      {data?.global?.uniqueTraits.toLocaleString() || 0}
                    </p>
                    <p className="text-xs text-zinc-600">Total Attributes</p>
                  </div>
                  <div className="bg-black/30 rounded-lg p-6 border border-[#9333EA]/10">
                    <p className="text-sm text-zinc-500 font-pixel mb-2">RAREST SCORE</p>
                    <p className="text-4xl font-pixel text-[#4AFF93] mb-1">
                      {data?.global?.rarestScore.toFixed(2) || 0}
                    </p>
                    <p className="text-xs text-zinc-600">Highest Rarity</p>
                  </div>
                  <div className="bg-black/30 rounded-lg p-6 border border-[#9333EA]/10">
                    <p className="text-sm text-zinc-500 font-pixel mb-2">AVERAGE SCORE</p>
                    <p className="text-4xl font-pixel text-[#4AFF93] mb-1">
                      {data?.global?.averageScore.toFixed(2) || 0}
                    </p>
                    <p className="text-xs text-zinc-600">Mean Rarity</p>
                  </div>
                </div>
              </div>

              {/* Chain Stats Grid */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {['chia', 'pulse', 'base'].map((chain) => (
                  <div key={chain} className="bg-black/50 backdrop-blur-sm border border-[#9333EA]/20 rounded-lg p-6">
                    <h3 className="text-lg font-pixel text-[#9333EA] mb-4 flex items-center justify-between">
                      <span>{chain === 'pulse' ? 'PULSECHAIN' : chain.toUpperCase()}</span>
                      <div className="px-2 py-1 bg-[#9333EA]/10 rounded text-xs">CHAIN</div>
                    </h3>
                    <div className="space-y-4">
                      <div className="flex justify-between items-baseline border-b border-[#9333EA]/10 pb-2">
                        <p className="text-sm text-zinc-500 font-pixel">Total NFTs</p>
                        <p className="text-xl font-pixel text-[#4AFF93]">
                          {data?.[chain]?.totalNFTs.toLocaleString() || 0}
                        </p>
                      </div>
                      <div className="flex justify-between items-baseline border-b border-[#9333EA]/10 pb-2">
                        <p className="text-sm text-zinc-500 font-pixel">Unique Traits</p>
                        <p className="text-xl font-pixel text-[#4AFF93]">
                          {data?.[chain]?.uniqueTraits.toLocaleString() || 0}
                        </p>
                      </div>
                      <div className="flex justify-between items-baseline border-b border-[#9333EA]/10 pb-2">
                        <p className="text-sm text-zinc-500 font-pixel">Rarest Score</p>
                        <p className="text-xl font-pixel text-[#4AFF93]">
                          {data?.[chain]?.rarestScore.toFixed(2) || 0}
                        </p>
                      </div>
                      <div className="flex justify-between items-baseline">
                        <p className="text-sm text-zinc-500 font-pixel">Average Score</p>
                        <p className="text-xl font-pixel text-[#4AFF93]">
                          {data?.[chain]?.averageScore.toFixed(2) || 0}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Search Section */}
            <div className="sticky top-0 z-50 -mx-4 px-4 py-4 bg-black/80 backdrop-blur-md border-b border-[#9333EA]/20">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Search by Token ID */}
                <div className="bg-black/50 backdrop-blur-sm border border-[#9333EA]/20 rounded-lg p-6">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-sm font-pixel text-[#9333EA]">SEARCH NFT BY TOKEN #</h3>
                    {searchResults.length > 0 && (
                      <button
                        onClick={clearSearch}
                        className="text-xs text-zinc-400 hover:text-[#9333EA] transition-colors"
                      >
                        CLEAR RESULTS
                      </button>
                    )}
                  </div>
                  <div className="flex space-x-2">
                    <div className="flex-1 relative">
                      <input
                        type="text"
                        value={searchTokenId}
                        onChange={(e) => setSearchTokenId(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleTokenSearch()}
                        className="w-full bg-black border border-[#9333EA]/20 rounded px-3 py-2 text-white font-pixel text-sm focus:outline-none focus:border-[#9333EA]"
                        placeholder="Enter token number (e.g. 1312)"
                      />
                      {searchTokenId && (
                        <button
                          onClick={() => {
                            setSearchTokenId('');
                            setSearchResults([]);
                          }}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-[#9333EA] transition-colors"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                          </svg>
                        </button>
                      )}
                    </div>
                    <button 
                      onClick={handleTokenSearch}
                      disabled={isSearching}
                      className="px-4 py-2 bg-[#9333EA] text-white font-pixel text-sm rounded hover:bg-[#9333EA]/80 transition-colors disabled:opacity-50"
                    >
                      {isSearching ? 'SEARCHING...' : 'SEARCH'}
                    </button>
                  </div>
                </div>

                {/* Search by Trait */}
                <div className="bg-black/50 backdrop-blur-sm border border-[#9333EA]/20 rounded-lg p-6">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-sm font-pixel text-[#9333EA]">SEARCH NFTs BY TRAIT VALUE</h3>
                    {searchResults.length > 0 && (
                      <button
                        onClick={clearSearch}
                        className="text-xs text-zinc-400 hover:text-[#9333EA] transition-colors"
                      >
                        CLEAR RESULTS
                      </button>
                    )}
                  </div>
                  <div className="flex space-x-2">
                    <div className="flex-1 relative">
                      <input
                        type="text"
                        value={searchTraitValue}
                        onChange={(e) => setSearchTraitValue(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleTraitSearch()}
                        className="w-full bg-black border border-[#9333EA]/20 rounded px-3 py-2 text-white font-pixel text-sm focus:outline-none focus:border-[#9333EA]"
                        placeholder="Enter trait value..."
                      />
                      {searchTraitValue && (
                        <button
                          onClick={() => setSearchTraitValue('')}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-[#9333EA] transition-colors"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                          </svg>
                        </button>
                      )}
                    </div>
                    <button 
                      onClick={handleTraitSearch}
                      disabled={isSearching}
                      className="px-4 py-2 bg-[#9333EA] text-white font-pixel text-sm rounded hover:bg-[#9333EA]/80 transition-colors disabled:opacity-50"
                    >
                      {isSearching ? 'SEARCHING...' : 'SEARCH'}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Search Results */}
            <SearchResults />

            {/* Distribution Charts */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Trait Distribution */}
              <div className="bg-black/50 backdrop-blur-sm border border-[#9333EA]/20 rounded-lg p-6">
                <h3 className="text-sm font-pixel text-[#9333EA] mb-4">TRAIT DISTRIBUTION</h3>
                <div className="space-y-4">
                  {data?.[selectedChain]?.traitDistribution.slice(0, 10).map((trait) => (
                    <div key={trait.name} className="space-y-1">
                      <div className="flex justify-between text-xs font-pixel">
                        <span className="text-zinc-400">{trait.name}</span>
                        <div className="flex space-x-4">
                          <span className="text-[#4AFF93]">{trait.count}</span>
                          <span className="text-zinc-500">{trait.percentage.toFixed(1)}%</span>
                          <span className="text-emerald-500">R: {trait.rarityScore.toFixed(1)}</span>
                        </div>
                      </div>
                      <div className="h-1 bg-zinc-900 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-[#4AFF93] rounded-full transition-all duration-500"
                          style={{ width: `${trait.percentage}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Trait Combinations */}
              <div className="bg-black/50 backdrop-blur-sm border border-[#9333EA]/20 rounded-lg p-6">
                <h3 className="text-sm font-pixel text-[#9333EA] mb-4">TRAIT COMBINATIONS ({selectedChain.toUpperCase()})</h3>
                <div className="space-y-4">
                  {data && data[selectedChain]?.traitCombinations && data[selectedChain].traitCombinations.length > 0 ? (
                    data[selectedChain].traitCombinations.map((combo) => {
                      const percentage = (combo.count / (data[selectedChain]?.totalNFTs || 1)) * 100;
                      return (
                        <div key={combo.traits} className="space-y-1">
                          <div className="flex justify-between text-xs font-pixel">
                            <span className="text-zinc-400">{combo.traits} Traits</span>
                            <div className="flex space-x-4">
                              <span className="text-[#4AFF93]">{combo.count.toLocaleString()} NFTs</span>
                              <span className="text-zinc-500">{percentage.toFixed(1)}%</span>
                            </div>
                          </div>
                          <div className="h-1 bg-zinc-900 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-[#4AFF93] rounded-full transition-all duration-500"
                              style={{ width: `${percentage}%` }}
                            />
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="text-zinc-500 text-sm">No trait combinations data available</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
} 